import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Star, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdsCarousel = ({ placement = 'homepage', autoPlay = true, interval = 5000 }) => {
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAds();
  }, [placement]);

  useEffect(() => {
    if (!autoPlay || ads.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoPlay, ads.length, interval]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/api/ads?placement=${placement}`);
      setAds(response.data);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (adId) => {
    try {
      await axios.post(`${API}/api/ads/${adId}/click`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  const handleAdClick = (ad) => {
    trackClick(ad.id);
    if (ad.link_url) {
      window.open(ad.link_url, '_blank');
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  };

  if (loading || ads.length === 0) return null;

  return (
    <div className="relative w-full overflow-hidden rounded-xl" data-testid="ads-carousel">
      <div className="relative h-48 sm:h-64 lg:h-72">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 cursor-pointer"
            onClick={() => handleAdClick(ads[currentIndex])}
          >
            <img
              src={ads[currentIndex].image_base64 || ads[currentIndex].image_url}
              alt={ads[currentIndex].title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            
            {/* Overlay with info */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-2">
                  {ads[currentIndex].is_featured && (
                    <span className="flex items-center gap-1 text-xs bg-yellow-500 text-black px-2 py-0.5 rounded font-medium">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                  )}
                  <span className="text-xs text-gray-300">
                    {ads[currentIndex].owner_name || 'BITZ Partner'}
                  </span>
                </div>
                <h3 className="text-white font-bold text-lg sm:text-xl mb-1">
                  {ads[currentIndex].title}
                </h3>
                {ads[currentIndex].description && (
                  <p className="text-gray-300 text-sm line-clamp-2">
                    {ads[currentIndex].description}
                  </p>
                )}
                {ads[currentIndex].link_url && (
                  <div className="flex items-center gap-1 text-yellow-400 text-sm mt-2">
                    <ExternalLink className="w-4 h-4" />
                    Learn More
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows */}
        {ads.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Dots Indicator */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-yellow-500' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsCarousel;
